import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { DealPaymentInstallment } from '../../../lib/types';

const round2 = (n: number) => Math.round(n * 100) / 100;
const fmt = (n: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number.isFinite(n) ? n : 0);

/** Quando cade la rata, leggibile. Rispecchia i tre modi che il CRM già salva. */
export function installmentWhen(inst: DealPaymentInstallment): string {
  if (inst.awaiting_approval) return 'data da destinarsi';
  if (inst.fixedDayOfMonth) {
    return `il ${inst.fixedDayOfMonth} di ogni mese${inst.nextMonth ? ' (dal mese successivo)' : ''}`;
  }
  const n = inst.daysAfterStart || 0;
  if (!n) return 'alla firma';
  if (inst.timeUnit === 'months') return `dopo ${n} ${n === 1 ? 'mese' : 'mesi'}`;
  return `dopo ${n} giorni`;
}

function amountOf(inst: DealPaymentInstallment, total: number): number {
  if (inst.type === 'percentage') return round2((total * (Number(inst.value) || 0)) / 100);
  return round2(Number(inst.value) || 0);
}

interface RateScheduleInlineProps {
  installments: DealPaymentInstallment[];
  /** Totale della riga: base + add-on, come fa il salvataggio. */
  total: number;
  onChange: (next: DealPaymentInstallment[]) => void;
  /** Bottone che apre il calcolatore completo nel foglio a tutta pagina. */
  onOpenFull?: () => void;
}

/**
 * Rate della riga come righe leggibili: si scorrono a colpo d'occhio e si
 * correggono sul posto, senza aprire il calcolatore. Sotto, la quadratura
 * rispetto al totale della riga — che è ciò su cui il CRM fa i conti.
 */
export function RateScheduleInline({ installments, total, onChange, onOpenFull }: RateScheduleInlineProps) {
  const [editing, setEditing] = useState<number | null>(null);

  const sum = round2(installments.reduce((s, inst) => s + amountOf(inst, total), 0));
  const balanced = Math.abs(sum - total) < 0.01;

  const patch = (i: number, next: Partial<DealPaymentInstallment>) => {
    onChange(
      installments.map((inst, idx) => {
        if (idx !== i) return inst;
        const merged = { ...inst, ...next } as DealPaymentInstallment;
        return { ...merged, amount: amountOf(merged, total) };
      }),
    );
  };

  const addRate = () => {
    onChange([
      ...installments,
      {
        type: 'percentage',
        value: 0,
        amount: 0,
        daysAfterStart: 30,
        description: '',
        isConfigurable: true,
        timeUnit: 'days',
      },
    ]);
    setEditing(installments.length);
  };

  const field =
    'px-1.5 py-1 text-[12px] tabular-nums rounded border border-black/[0.12] dark:border-white/15 bg-white dark:bg-gray-800/60 text-black dark:text-white focus:border-[#2383E2] focus:outline-none focus:ring-2 focus:ring-[#2383E2]/20 transition-colors';

  return (
    <div>
      {installments.map((inst, i) => {
        const value = amountOf(inst, total);
        if (editing === i) {
          const mode: 'after' | 'day' | 'tbd' = inst.awaiting_approval
            ? 'tbd'
            : inst.fixedDayOfMonth
              ? 'day'
              : 'after';
          return (
            <div
              key={i}
              className="flex items-center gap-1.5 flex-wrap px-2 py-1.5 my-0.5 rounded bg-black/[0.03] dark:bg-white/[0.05]"
            >
              <select
                className={field}
                value={inst.type}
                onChange={(e) => patch(i, { type: e.target.value as 'fixed' | 'percentage' })}
              >
                <option value="percentage">%</option>
                <option value="fixed">€</option>
              </select>
              <input
                className={`${field} w-[62px]`}
                inputMode="decimal"
                value={inst.value ?? 0}
                onFocus={(e) => e.target.select()}
                onChange={(e) => patch(i, { value: parseFloat(e.target.value.replace(',', '.')) || 0 })}
              />
              <select
                className={field}
                value={mode}
                onChange={(e) => {
                  const m = e.target.value;
                  if (m === 'tbd') patch(i, { awaiting_approval: true, fixedDayOfMonth: undefined });
                  else if (m === 'day')
                    patch(i, { awaiting_approval: false, fixedDayOfMonth: inst.fixedDayOfMonth || 1, daysAfterStart: 0 });
                  else patch(i, { awaiting_approval: false, fixedDayOfMonth: undefined, daysAfterStart: inst.daysAfterStart || 0 });
                }}
              >
                <option value="after">dopo…</option>
                <option value="day">giorno fisso</option>
                <option value="tbd">da destinarsi</option>
              </select>
              {mode === 'after' && (
                <>
                  <input
                    className={`${field} w-[58px]`}
                    inputMode="numeric"
                    value={inst.daysAfterStart ?? 0}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => patch(i, { daysAfterStart: parseInt(e.target.value, 10) || 0 })}
                  />
                  <select
                    className={field}
                    value={inst.timeUnit || 'days'}
                    onChange={(e) => patch(i, { timeUnit: e.target.value as 'days' | 'months' })}
                  >
                    <option value="days">giorni</option>
                    <option value="months">mesi</option>
                  </select>
                </>
              )}
              {mode === 'day' && (
                <>
                  <span className="text-[11px] text-black/50 dark:text-white/50">il giorno</span>
                  <input
                    className={`${field} w-[52px]`}
                    inputMode="numeric"
                    value={inst.fixedDayOfMonth ?? 1}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) =>
                      patch(i, { fixedDayOfMonth: Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)) })
                    }
                  />
                </>
              )}
              <input
                className={`${field} flex-1 min-w-[120px]`}
                value={inst.description || ''}
                placeholder="Descrizione della rata"
                onChange={(e) => patch(i, { description: e.target.value })}
              />
              <span className="text-[11px] text-black/50 dark:text-white/50 tabular-nums">{fmt(value)}</span>
              <button
                type="button"
                title="Elimina rata"
                onClick={() => {
                  onChange(installments.filter((_, idx) => idx !== i));
                  setEditing(null);
                }}
                className="p-1 rounded text-black/35 dark:text-white/35 hover:text-red-600 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
              >
                <Trash2 size={13} />
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-[12px] font-medium px-2.5 py-1 rounded-md bg-[#37352F] text-white hover:opacity-90 transition-opacity"
              >
                Fatto
              </button>
            </div>
          );
        }

        return (
          <button
            key={i}
            type="button"
            onClick={() => setEditing(i)}
            className="group w-full flex items-center min-h-[30px] px-2 py-1 -mx-2 rounded text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.04] transition-colors"
          >
            <span className="w-[152px] flex-shrink-0 text-[12px] font-medium text-black/55 dark:text-white/55">
              Rata {i + 1}
            </span>
            <span className="flex-1 min-w-0 text-[13px] text-black dark:text-white truncate tabular-nums">
              {inst.type === 'percentage' ? `${inst.value}%` : fmt(Number(inst.value) || 0)} · {installmentWhen(inst)}
              <span className="text-black/40 dark:text-white/40 text-[12px]">
                {' '}
                · {fmt(value)}
                {inst.description ? ` · ${inst.description}` : ''}
              </span>
            </span>
            <span className="ml-auto flex-shrink-0 text-[11px] text-black/50 dark:text-white/50 opacity-0 group-hover:opacity-100 transition-opacity">
              modifica
            </span>
          </button>
        );
      })}

      <div className="flex items-center gap-2 px-2 py-1.5 -mx-2 text-[12px] tabular-nums text-black/55 dark:text-white/55">
        <span>
          Somma rate {fmt(sum)} su {fmt(total)}
        </span>
        <span className="flex-1 min-w-[60px] h-[3px] rounded bg-black/[0.08] dark:bg-white/[0.12] overflow-hidden">
          <span
            className={`block h-full ${balanced ? 'bg-[#0F7B6C]' : 'bg-[#CB912F]'}`}
            style={{ width: `${Math.min(100, total > 0 ? (sum / total) * 100 : 0)}%` }}
          />
        </span>
        <span className={`font-medium ${balanced ? 'text-[#0F7B6C]' : 'text-[#B06C00] dark:text-[#E0A030]'}`}>
          {balanced
            ? 'quadra'
            : sum < total
              ? `mancano ${fmt(round2(total - sum))}`
              : `eccedono ${fmt(round2(sum - total))}`}
        </span>
      </div>

      <div className="flex items-center gap-2 pt-0.5">
        <button
          type="button"
          onClick={addRate}
          className="text-[12px] font-medium px-2.5 py-1 rounded-md border border-black/[0.12] dark:border-white/15 text-black dark:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
        >
          + Aggiungi rata
        </button>
        {onOpenFull && (
          <button
            type="button"
            onClick={onOpenFull}
            className="text-[12px] font-medium text-[#2383E2] hover:underline px-1 py-1"
          >
            apri in grande
          </button>
        )}
      </div>
    </div>
  );
}
